import type { Invoice, PayoutPartyResponse } from 'payments-api/model';
import type { Party } from 'payments-api/model/party';
import React, { useMemo, useState } from 'react';
import { makePath } from 'utils/routes';

import type { ColumnDefinition } from '@fragment/ui/src/components/Table/helpers';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

// Helper to check if payout party matches a given externalId
const isPayoutPartyMatch = (
  payoutParty: PayoutPartyResponse,
  externalId: string
): boolean => {
  if ('platform' in payoutParty && payoutParty.platform) {
    return false; // Platform payout doesn't match any party
  }
  return 'party_id' in payoutParty && payoutParty.party_id === externalId;
};

// Helper to get display string for payout party
const getPayoutPartyDisplay = (payoutParty: PayoutPartyResponse): string => {
  if ('platform' in payoutParty && payoutParty.platform) {
    return 'Platform';
  }
  return 'party_id' in payoutParty ? payoutParty.party_id : 'Unknown';
};

type InvoiceRow = {
  id: string;
  ik: string;
  created: string;
  totalForParty: bigint;
};

type ItemRow = {
  id: string;
  description: string;
  productId: string;
  payoutParty: string; // Display string: 'Platform' or party_id
  amount: string;
};

type PartyViewProps = {
  party: Party;
  partyInvoices: Invoice[];
  externalId: string;
  workspaceId: string;
  /**
   * If true, invoices are clickable and navigate to invoice detail
   * @default true
   */
  enableInvoiceNavigation?: boolean;
};

// Format amount as currency
const formatCurrency = (amount: string | bigint): string => {
  const amountNum =
    typeof amount === 'string' ? parseInt(amount, 10) : Number(amount);
  return `$${(amountNum / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Define schema for items table
const itemsSchema: readonly ColumnDefinition<ItemRow>[] = [
  {
    title: 'Description',
    format: (row: ItemRow) => row.description,
  },
  {
    title: 'Seller',
    format: (row: ItemRow) => (
      <span className="text-main-500">{row.payoutParty}</span>
    ),
  },
  {
    title: 'Amount',
    justify: 'right' as const,
    format: (row: ItemRow) => formatCurrency(row.amount),
  },
];

/**
 * PartyView component - Shared logic and UI for displaying party information
 * Used by both PartyDetailPage (internal) and PublicPartyViewPage (public)
 */
export const PartyView = ({
  party,
  partyInvoices,
  externalId,
  workspaceId,
  enableInvoiceNavigation = true,
}: PartyViewProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<'invoices' | 'items'>('invoices');

  const tabOptions = useMemo(
    () => [
      { label: 'Invoices', value: 'invoices' as const },
      { label: 'Items', value: 'items' as const },
    ],
    []
  );

  // Prepare invoice data for SubTable
  const invoiceRows = useMemo(
    (): InvoiceRow[] =>
      partyInvoices.map((invoice) => {
        // Check if this party is the buyer or involved as a seller
        const isBuyer = invoice.buyerParty === externalId;
        const partyItemsInInvoice =
          invoice.lineItems?.filter(
            (item) =>
              isBuyer || isPayoutPartyMatch(item.payout_party, externalId)
          ) || [];

        const totalForParty = partyItemsInInvoice.reduce(
          (sum, item) => sum + BigInt(item.amount),
          BigInt(0)
        );

        return {
          id: invoice.id,
          ik: invoice.ik,
          created: invoice.created,
          totalForParty,
        };
      }),
    [partyInvoices, externalId]
  );

  // Prepare item data for SubTable
  const itemRows = useMemo(
    (): ItemRow[] =>
      partyInvoices.flatMap((invoice) => {
        const isBuyer = invoice.buyerParty === externalId;
        return (invoice.lineItems || [])
          .filter(
            (item) =>
              isBuyer || isPayoutPartyMatch(item.payout_party, externalId)
          )
          .map((item) => ({
            id: item.id,
            description: item.description,
            productId: item.product_id,
            payoutParty: getPayoutPartyDisplay(item.payout_party),
            amount: item.amount,
          }));
      }),
    [partyInvoices, externalId]
  );

  // Define schema for invoices table
  const invoicesSchema: readonly ColumnDefinition<InvoiceRow>[] = useMemo(
    () => [
      {
        title: 'Invoice',
        format: (row: InvoiceRow) => row.ik,
      },
      {
        title: 'Date',
        format: (row: InvoiceRow) =>
          new Date(row.created).toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
          }),
      },
      {
        title: 'Amount',
        justify: 'right' as const,
        format: (row: InvoiceRow) => formatCurrency(row.totalForParty),
      },
    ],
    []
  );

  const getInvoicePath = (invoice: InvoiceRow): string => {
    if (enableInvoiceNavigation) {
      return makePath(['w', workspaceId, 'payments', 'invoices', invoice.id], {
        absolute: true,
      });
    }
    // Navigate to public invoice view
    return makePath(
      [
        'w',
        workspaceId,
        'payments',
        'parties',
        externalId,
        'invoices',
        invoice.id,
      ],
      { absolute: true }
    );
  };

  const getItemPath = (_item: ItemRow): string =>
    // Future: navigate to item detail or show item details
    // For now, return current path to make rows non-navigable
    '#';
  return (
    <>
      {/* Party Name Header */}
      <div className="border-[0.5px] border-main-300 w-full flex items-center">
        <div className="h-f10 w-f10 border-r-[0.5px] border-main-300" />
        <div className="flex items-center w-full h-full pl-f2">
          {party.externalId}
        </div>
      </div>

      {/* Tabs and Content */}
      <div className="pt-f4">
        <div className="flex flex-col w-full  min-h-f1 ">
          {/* Tabs */}
          <TabsV2
            options={tabOptions}
            selectedValue={activeTab}
            onClick={setActiveTab}
            data-testid="party-view-tabs"
          />

          <div>
            {/* Tab Content */}
            {activeTab === 'invoices' && (
              <NavTableV2
                data={invoiceRows}
                schema={invoicesSchema}
                getPath={getInvoicePath}
                textSize="md"
                showHeader={false}
                stickyHeader={false}
                emptyValue={{
                  Invoice: (
                    <span className="text-main-500">
                      No invoices found for this party
                    </span>
                  ),
                  Date: '',
                  Amount: '',
                }}
              />
            )}

            {activeTab === 'items' && (
              <NavTableV2
                data={itemRows}
                schema={itemsSchema}
                getPath={getItemPath}
                textSize="md"
                showHeader={false}
                stickyHeader={false}
                emptyValue={{
                  Description: (
                    <span className="text-main-500">
                      No items found for this party
                    </span>
                  ),
                  Seller: '',
                  Amount: '',
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
